import { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Check, ChevronLeft, ChevronRight, Plus } from 'lucide-react'
import { COURSES, COURSE_LABELS, MEAL_SLOT_LABELS, type Course, type MealSlot } from '@shared/events'
import { planBySlot, plannedDishes, recipesById } from '@shared/plate'
import { addDays, startOfWeek, weekDays, weekdayLabel, type DayKey, type WeekKey } from '@shared/time'
import { planificar, quitarPlato } from '../lib/acciones'
import { useEvents } from '../lib/hooks'
import { esElMismoPlato, nombreDePlato } from '../lib/platos'
import { useMostrarExtras } from '../lib/preferencias'
import { useSession } from '../lib/session'
import { useAviso } from './Aviso'
import { Chip, type Eleccion } from './ElegirReceta'
import { BotonIcono } from './ui'
import { Hoja } from './Hoja'

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

function rotuloSemana(weekKey: WeekKey, today: DayKey): string {
  const desde = startOfWeek(today)
  if (weekKey === desde) return 'Esta semana'
  if (weekKey === addDays(desde, 7)) return 'La que viene'
  if (weekKey === addDays(desde, -7)) return 'La pasada'
  const fin = addDays(weekKey, 6)
  return `${Number(weekKey.slice(8))} ${MESES[Number(weekKey.slice(5, 7)) - 1]} – ${Number(fin.slice(8))} ${MESES[Number(fin.slice(5, 7)) - 1]}`
}

/**
 * Poner un plato en la semana: una cuadrícula de días por franjas y un toque
 * en el hueco. Tocar donde ya está lo quita, y tocar donde hay otro lo cambia.
 *
 * La hoja se queda abierta para poder poner el mismo plato en varios días; cada
 * toque es su propio evento y su propio "Deshacer".
 */
export function PonerPlato({
  eleccion,
  cursoInicial,
  encaja,
  weekKeyInicial,
  today,
  onCerrar,
}: {
  eleccion: Eleccion
  cursoInicial: Course
  /** Las franjas en las que la receta dice que encaja; vacío, en todas. */
  encaja: readonly MealSlot[]
  weekKeyInicial: WeekKey
  today: DayKey
  onCerrar: () => void
}) {
  const events = useEvents()
  const { user } = useSession()
  const avisar = useAviso()
  const { franjas } = useMostrarExtras()
  const [weekKey, setWeekKey] = useState(weekKeyInicial)
  const [curso, setCurso] = useState<Course>(cursoInicial)

  const plan = useMemo(() => planBySlot(events), [events])
  const recipes = useMemo(() => recipesById(events), [events])
  const dias = useMemo(() => weekDays(weekKey), [weekKey])
  const nombre = nombreDePlato(eleccion, recipes)

  async function tocar(dayKey: DayKey, slot: MealSlot): Promise<void> {
    if (!user) return
    const dishes = plannedDishes(plan, dayKey, slot)
    const donde = `${weekdayLabel(dayKey)} ${Number(dayKey.slice(8))}, ${MEAL_SLOT_LABELS[slot].toLowerCase()}`
    const puesto = dishes.find((dish) => esElMismoPlato(dish, eleccion))
    const deshacer = puesto
      ? await quitarPlato(events, dayKey, slot, puesto.course)
      : await planificar(events, user.id, dayKey, slot, curso, eleccion)
    avisar(puesto ? `${nombre} fuera del ${donde}` : `${nombre} el ${donde}`, {
      label: 'Deshacer',
      onClick: () => void deshacer(),
    })
  }

  return (
    <Hoja
      titulo={nombre}
      descripcion="Toca un hueco para ponerlo. Tócalo otra vez para quitarlo."
      onCerrar={onCerrar}
    >
      <div className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1" role="radiogroup" aria-label="Qué plato es">
        {COURSES.map((value) => (
          <Chip key={value} activo={curso === value} onClick={() => setCurso(value)}>
            {COURSE_LABELS[value]}
          </Chip>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <BotonIcono label="Semana anterior" onClick={() => setWeekKey((week) => addDays(week, -7))}>
          <ChevronLeft className="h-4 w-4" />
        </BotonIcono>
        <span className="label-caps text-muted-foreground" aria-live="polite">
          {rotuloSemana(weekKey, today)}
        </span>
        <BotonIcono label="Semana siguiente" onClick={() => setWeekKey((week) => addDays(week, 7))}>
          <ChevronRight className="h-4 w-4" />
        </BotonIcono>
      </div>

      <div
        className="mt-3 grid gap-1.5 pb-2"
        style={{ gridTemplateColumns: `3.25rem repeat(${franjas.length}, minmax(0, 1fr))` }}
      >
        <span />
        {franjas.map((slot) => (
          <span key={slot} className="label-caps truncate text-center text-muted-foreground">
            {MEAL_SLOT_LABELS[slot]}
          </span>
        ))}

        {dias.map((dayKey) => {
          const esHoy = dayKey === today
          return (
            <div key={dayKey} className="contents">
              <span
                className={`flex flex-col justify-center leading-tight ${
                  esHoy ? 'text-primary' : dayKey < today ? 'text-muted-foreground' : ''
                }`}
              >
                <span className="text-xs first-letter:uppercase">{weekdayLabel(dayKey).slice(0, 3)}</span>
                <span className="font-display text-lg">{Number(dayKey.slice(8))}</span>
              </span>
              {franjas.map((slot) => {
                const dishes = plannedDishes(plan, dayKey, slot)
                const puesto = dishes.find((dish) => esElMismoPlato(dish, eleccion))
                const ocupante = dishes.find((dish) => dish.course === curso)
                const dia = `${weekdayLabel(dayKey)} ${Number(dayKey.slice(8))}`
                const franja = MEAL_SLOT_LABELS[slot].toLowerCase()
                const fuera = encaja.length > 0 && !encaja.includes(slot)
                return (
                  <motion.button
                    key={slot}
                    type="button"
                    whileTap={{ scale: 0.95 }}
                    onClick={() => void tocar(dayKey, slot)}
                    aria-pressed={Boolean(puesto)}
                    aria-label={
                      puesto
                        ? `Quitar del ${dia}, ${franja}`
                        : ocupante
                          ? `Cambiar ${nombreDePlato(ocupante, recipes)} por ${nombre} el ${dia}, ${franja}`
                          : `Poner el ${dia}, ${franja}`
                    }
                    className={`flex min-h-14 min-w-0 items-center justify-center rounded-2xl border px-1.5 py-1 text-center outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring ${
                      puesto
                        ? 'border-sage/50 bg-filled text-sage-deep'
                        : ocupante
                          ? 'border-border bg-card text-muted-foreground hover:border-primary/40'
                          : `border-dashed hover:border-primary/50 hover:bg-accent/60 ${
                              fuera ? 'border-border/50 text-muted-foreground/40' : 'border-border text-muted-foreground'
                            }`
                    }`}
                  >
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.span
                        key={puesto ? 'puesto' : ocupante ? `otro:${ocupante.recipeId}:${ocupante.freeText}` : 'libre'}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.15 }}
                        className="flex min-w-0 flex-col items-center"
                      >
                        {puesto ? (
                          <>
                            <Check className="h-4 w-4" strokeWidth={2.6} />
                            {puesto.course !== curso && (
                              <span className="text-[0.6rem] font-medium uppercase tracking-wider">
                                {COURSE_LABELS[puesto.course]}
                              </span>
                            )}
                          </>
                        ) : ocupante ? (
                          <span className="line-clamp-2 break-words text-[0.7rem] leading-tight">
                            {nombreDePlato(ocupante, recipes)}
                          </span>
                        ) : (
                          <Plus className="h-4 w-4" strokeWidth={2.4} />
                        )}
                      </motion.span>
                    </AnimatePresence>
                  </motion.button>
                )
              })}
            </div>
          )
        })}
      </div>
    </Hoja>
  )
}
